import { ReactNode } from 'react';
import { Info, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';

export type InfoBoxVariant = 'info' | 'warning' | 'error' | 'success';

export interface InfoBoxProps {
  variant?: InfoBoxVariant;
  children: ReactNode;
  title?: string;
  className?: string;
  icon?: ReactNode;
}

const variantConfig: Record<InfoBoxVariant, { bgColor: string; borderColor: string; textColor: string; icon: ReactNode }> = {
  info: {
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-800',
    icon: <Info className="h-5 w-5 text-blue-400" />,
  },
  warning: {
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-800',
    icon: <AlertTriangle className="h-5 w-5 text-amber-400" />,
  },
  error: {
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    icon: <AlertCircle className="h-5 w-5 text-red-400" />,
  },
  success: {
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
    icon: <CheckCircle className="h-5 w-5 text-green-400" />,
  },
};

/**
 * Info box component for displaying contextual information
 */
export function InfoBox({ variant = 'info', children, title, className = '', icon }: InfoBoxProps) {
  const config = variantConfig[variant];

  return (
    <div data-tsx-id="info-box" className={`rounded-md border ${config.bgColor} ${config.borderColor} p-4 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {icon || config.icon}
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${config.textColor} mb-2`}>
              {title}
            </h3>
          )}
          <div className={`text-sm ${config.textColor}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
